import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../api";
import Layout from "../components/Layout";
import { toasts } from "../components/AppToast";
import Pagination from "../components/Pagination";

import {
  FiTrash2,
  FiEdit,
  FiEye,
  FiUsers,
  FiCheckCircle,
  FiShield,
  FiUser,
  FiXCircle,
  FiSearch,
  FiLock,
  FiPhoneCall,
  FiHome,
  FiActivity,
  FiCalendar,
  FiMail,
} from "react-icons/fi";

export default function UserManagement() {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search] = useState("");
  const [typeF, setTypeF] = useState("all");
  const [statusF, setStatusF] = useState("all");
  
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(null);
  const [selUser, setSelUser] = useState(null);
  const [viewMods, setViewMods] = useState([]);
  const [allMods, setAllMods] = useState([]);
  const [saving, setSaving] = useState(false);

  const PER = 10;

  // =========================================
  // INITIAL LOAD
  // =========================================

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login");
      return;
    }
  }, [navigate]);

  // =========================================
  // TOAST
  // =========================================

  const showToast = (msg, type = "success") => {
    if (type === "error") {
      toasts.error(msg);
    } else if (type === "warning") {
      toasts.warning(msg);
    } else if (type === "info") {
      toasts.info(msg);
    } else {
      toasts.success(msg);
    }
  };

  // =========================================
  // FETCH USERS
  // =========================================

  const fetchUsers = useCallback(async () => {
    setLoading(true);

    try {
      const r = await API.get("/users");

      setUsers(r.data || []);
    } catch (e) {
      console.error(e);

      showToast(
        e.response?.data?.message ||
          "Failed to load users",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // =========================================
  // FETCH MODULES
  // =========================================

  const fetchMods = useCallback(async () => {
    try {
      const r = await API.get("/users/modules");

      setAllMods(r.data || []);
    } catch (e) {
      console.error(e);

      showToast(
        e.response?.data?.message ||
          "Failed to load modules",
        "error"
      );
    }
  }, []);

  // =========================================
  // FETCH USER MODULES
  // =========================================

  const fetchUserMods = async (uid) => {
    try {
      const r = await API.get(`/users/${uid}/modules`);

      return (r.data || []).map(Number);
    } catch (e) {
      console.error(e);

      showToast(
        e.response?.data?.message ||
          "Failed to load user modules",
        "error"
      );

      return [];
    }
  };

  // =========================================
  // LOAD DATA
  // =========================================

  useEffect(() => {
    fetchUsers();
    fetchMods();
  }, [fetchUsers, fetchMods]);

  // =========================================
  // ADD USER
  // =========================================

  const openAdd = () => {
    navigate("/user-management/add");
  };

  // =========================================
  // EDIT USER
  // =========================================

  const openEdit = (user) => {
    navigate("/user-management/edit", {
      state: {
        userId: user.user_id,
      },
    });
  };

  // =========================================
  // VIEW USER
  // =========================================

  const openView = async (user) => {
    setSelUser(user);

    const mods = await fetchUserMods(user.user_id);

    setViewMods(mods);

    setModal("view");
  };

  // =========================================
  // DELETE USER MODAL
  // =========================================

  const openDel = (user) => {
    setSelUser(user);

    setModal("delete");
  };

  // =========================================
  // DELETE USER
  // =========================================

  const handleDelete = async () => {
    if (!selUser) {
      return;
    }

    setSaving(true);

    try {
      await API.delete(
        `/users/${selUser.user_id}`
      );

      showToast(
        "User deleted successfully",
        "success"
      );

      setModal(null);
      setSelUser(null);

      await fetchUsers();

      // Keep page valid after deletion
      if (
        page > 1 &&
        paged.length === 1
      ) {
        setPage((p) => Math.max(1, p - 1));
      }
    } catch (e) {
      console.error(e);

      showToast(
        e.response?.data?.message ||
          "Delete failed",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  // =========================================
  // STATUS TOGGLE
  // =========================================

  const handleToggle = async (user) => {
    try {
      const newStatus =
        user.user_status === 1 ? 2 : 1;

      await API.patch(
        `/users/${user.user_id}/status`,
        {
          status: newStatus,
        }
      );

      if (newStatus === 1) {
        showToast(
          "User activated successfully",
          "success"
        );
      } else {
        showToast(
          "User deactivated successfully",
          "success"
        );
      }

      await fetchUsers();
    } catch (e) {
      console.error(e);

      showToast(
        e.response?.data?.message ||
          "Failed to update status",
        "error"
      );
    }
  };

  // =========================================
  // FILTER
  // =========================================

  const filtered = users.filter((u) => {
    const q = search.toLowerCase().trim();

    const matchesSearch =
      !q ||
      u.user_name
        ?.toLowerCase()
        .includes(q) ||
      u.user_emailId
        ?.toLowerCase()
        .includes(q) ||
      u.user_loginId
        ?.toLowerCase()
        .includes(q);

    const matchesType =
      typeF === "all" ||
      String(u.user_type) === typeF;

    const matchesStatus =
      statusF === "all" ||
      String(u.user_status) === statusF;

    return (
      matchesSearch &&
      matchesType &&
      matchesStatus
    );
  });

  // =========================================
  // PAGINATION
  // =========================================
  const total = filtered.length;
  const totalPages = Math.ceil(
    filtered.length / PER
  );

  const paged = filtered.slice(
    (page - 1) * PER,
    page * PER
  );


// =========================================================
  // PAGINATION - PREVIOUS
  // =========================================================

  const handlePrevious = () => {
    if (
      loading ||
      page <= 1
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.max(
          1,
          prev - 1
        )
    );
  };

  // =========================================================
  // PAGINATION - NEXT
  // =========================================================

  const handleNext = () => {
    if (
      loading ||
      totalPages <= 0 ||
      page >= totalPages
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.min(
          totalPages,
          prev + 1
        )
    );
  };


  // =========================================
  // RETURN
  // =========================================

  return (
    <Layout activePage="User Management">
      <main className="dashboard-main">

        {/* =====================================
            STAT CARDS
        ====================================== */}

        <div className="dashboard-stat-grid row">

          {/* TOTAL */}

          <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6">
            <div
              className={`dashboard-stat-card stat-available ${
                typeF === "all" &&
                statusF === "all"
                  ? "active"
                  : ""
              }`}
              onClick={() => {
                setTypeF("all");
                setStatusF("all");
                setPage(1);
              }}
            >
              <div className="dashboard-stat-top">

                <div className="dashboard-stat-icon">
                  <FiUsers color="#63b3ed" />
                </div>

                <div className="dashboard-stat-label">
                  Total
                </div>

                <div className="dashboard-stat-badge">
                  {users.length}
                </div>

              </div>
            </div>
          </div>

          {/* ADMIN */}

          <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6">
            <div
              className={`dashboard-stat-card stat-available ${
                typeF === "1" &&
                statusF === "all"
                  ? "active"
                  : ""
              }`}
              onClick={() => {
                setTypeF("1");
                setStatusF("all");
                setPage(1);
              }}
            >
              <div className="dashboard-stat-top">

                <div className="dashboard-stat-icon">
                  <FiShield color="#63b3ed" />
                </div>

                <div className="dashboard-stat-label">
                  Admin
                </div>

                <div className="dashboard-stat-badge">
                  {
                    users.filter(
                      (u) => u.user_type === 1
                    ).length
                  }
                </div>

              </div>
            </div>
          </div>

          {/* GENERAL */}

          <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6">
            <div
              className={`dashboard-stat-card stat-available ${
                typeF === "2" &&
                statusF === "all"
                  ? "active"
                  : ""
              }`}
              onClick={() => {
                setTypeF("2");
                setStatusF("all");
                setPage(1);
              }}
            >
              <div className="dashboard-stat-top">

                <div className="dashboard-stat-icon">
                  <FiUser color="#48bb78" />
                </div>

                <div className="dashboard-stat-label">
                  General
                </div>

                <div className="dashboard-stat-badge">
                  {
                    users.filter(
                      (u) => u.user_type === 2
                    ).length
                  }
                </div>

              </div>
            </div>
          </div>

          {/* ACTIVE */}

          <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6">
            <div
              className={`dashboard-stat-card stat-available ${
                typeF === "all" &&
                statusF === "1"
                  ? "active"
                  : ""
              }`}
              onClick={() => {
                setTypeF("all");
                setStatusF("1");
                setPage(1);
              }}
            >
              <div className="dashboard-stat-top">

                <div className="dashboard-stat-icon">
                  <FiCheckCircle color="#48bb78" />
                </div>

                <div className="dashboard-stat-label">
                  Active
                </div>

                <div className="dashboard-stat-badge">
                  {
                    users.filter(
                      (u) => u.user_status === 1
                    ).length
                  }
                </div>

              </div>
            </div>
          </div>

          {/* INACTIVE */}

          <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6">
            <div
              className={`dashboard-stat-card stat-available ${
                typeF === "all" &&
                statusF === "2"
                  ? "active"
                  : ""
              }`}
              onClick={() => {
                setTypeF("all");
                setStatusF("2");
                setPage(1);
              }}
            >
              <div className="dashboard-stat-top">

                <div className="dashboard-stat-icon">
                  <FiXCircle color="#fc8181" />
                </div>

                <div className="dashboard-stat-label">
                  Inactive
                </div>

                <div className="dashboard-stat-badge">
                  {
                    users.filter(
                      (u) => u.user_status === 2
                    ).length
                  }
                </div>

              </div>
            </div>
          </div>

          {/* ADD USER */}

          <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6">
            <div className="dashboard-stat-cardbtn">
              <button
                type="button"
                className="um-btn-primary"
                onClick={openAdd}
              >
                <span className="um-add-icon">
                  ＋
                </span>

                Add User
              </button>
            </div>
          </div>

        </div>

        {/* =====================================
            TABLE
        ====================================== */}

        <div className="table-responsive">

          <table className="table table-striped table-hover table-bordered align-middle mb-0">

            <thead className="table-light">

              <tr className="um-table-head">

                <th className="um-th text-center">
                  #
                </th>

                <th className="um-th">
                  Name
                </th>

                <th className="um-th">
                  Login ID
                </th>

                <th className="um-th">
                  Email
                </th>

                <th className="um-th text-center">
                  Type
                </th>

                <th className="um-th text-center">
                  Status
                </th>

                <th className="um-th text-center">
                  Actions
                </th>

              </tr>

            </thead>

            <tbody>

              {/* LOADING */}

              {loading ? (
                Array.from({
                  length: 6,
                }).map((_, i) => (
                  <tr key={i}>

                    {Array.from({
                      length: 7,
                    }).map((_, j) => (
                      <td
                        key={j}
                        className="um-td"
                      >
                        <div className="skeleton" />
                      </td>
                    ))}

                  </tr>
                ))
              ) : paged.length === 0 ? (

                /* EMPTY */

                <tr>

                  <td
                    colSpan={7}
                    className="empty-table-cell text-center py-5"
                  >
                    <div className="empty-icon">
                      <FiSearch size={17} />
                    </div>

                    <div className="empty-title">
                      No users found
                    </div>

                  </td>

                </tr>

              ) : (

                /* USERS */

                paged.map((u, i) => (

                  <tr
                    key={u.user_id}
                    className="um-row"
                  >

                    {/* NUMBER */}

                    <td className="um-td text-center">
                      {(page - 1) * PER +
                        i +
                        1}
                    </td>

                    {/* NAME */}

                    <td className="um-td">

                      <div className="user-name-wrap">

                        <div
                          className={`um-mini-avatar ${
                            u.user_type === 1
                              ? "avatar-admin"
                              : "avatar-general"
                          }`}
                        >
                          {u.user_name
                            ?.charAt(0)
                            .toUpperCase()}
                        </div>

                        <div>

                          <div className="user-name">
                            {u.user_name}
                          </div>

                          <div className="user-phone">
                            {u.user_telno || "—"}
                          </div>

                        </div>

                      </div>

                    </td>

                    {/* LOGIN */}

                    <td className="um-td">
                      <span className="badge badge-success">
                        {u.user_loginId}
                      </span>
                    </td>

                    {/* EMAIL */}

                    <td className="um-td user-email">
                      {u.user_emailId}
                    </td>

                    {/* TYPE */}

                    <td className="um-td text-center">

                      <span
                        className={`badge ${
                          u.user_type === 1
                            ? "badge-admin"
                            : "badge-general"
                        }`}
                      >
                        {u.user_type === 1
                          ? "Admin"
                          : "General"}
                      </span>

                    </td>

                    {/* STATUS */}

                    <td className="um-td">

                      <div className="status-wrap">

                        <div
                          className={`status-toggle ${
                            u.user_status === 1
                              ? "status-active"
                              : "status-inactive"
                          }`}
                          onClick={() =>
                            handleToggle(u)
                          }
                        >
                          <div
                            className={`status-knob ${
                              u.user_status === 1
                                ? "knob-active"
                                : "knob-inactive"
                            }`}
                          />
                        </div>

                        <span
                          className={`status-text ${
                            u.user_status === 1
                              ? "text-active"
                              : "text-inactive"
                          }`}
                        >
                          {u.user_status === 1
                            ? "Active"
                            : "Inactive"}
                        </span>

                      </div>

                    </td>

                    {/* ACTIONS */}

                    <td className="um-td">

                      <div className="action-buttons d-flex justify-content-center gap-2">

                        {/* VIEW */}

                        <button
                          type="button"
                          className="action-btn action-view btn btn-sm"
                          title="View"
                          onClick={() =>
                            openView(u)
                          }
                        >
                          <FiEye />
                        </button>

                        {/* EDIT */}

                        <button
                          type="button"
                          className="action-btn action-edit btn btn-sm"
                          title="Edit"
                          onClick={() =>
                            openEdit(u)
                          }
                        >
                          <FiEdit />
                        </button>

                        {/* DELETE */}

                        <button
                          type="button"
                          className="action-btn action-delete btn btn-sm"
                          title="Delete"
                          onClick={() =>
                            openDel(u)
                          }
                        >
                          <FiTrash2 />
                        </button>

                      </div>

                    </td>

                  </tr>

                ))

              )}

            </tbody>

          </table>

          {/* =====================================
              PAGINATION
          ====================================== */}

            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={10}
              loading={loading}
              onPrevious={handlePrevious}
              onNext={handleNext}
            />

        </div>

      </main>

      {/* =========================================
          VIEW MODAL
      ========================================== */}

      {modal === "view" && selUser && (

        <div
          className="um-overlay"
          onClick={() => setModal(null)}
        >

          <div
            className="um-modal um-view-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            <div className="um-modal-head">

              <h2 className="um-modal-title">
                <FiUser/> User Profile
              </h2>

              <button
                className="um-x-btn"
                onClick={() =>
                  setModal(null)
                }
              >
                ✕
              </button>

            </div>

            <div className="um-modal-scroll">

              {/* PROFILE */}

              <div className="profile-header">

                <div
                  className={`um-big-avatar ${
                    selUser.user_type === 1
                      ? "avatar-admin"
                      : "avatar-general"
                  }`}
                >
                  {selUser.user_name
                    ?.charAt(0)
                    .toUpperCase()}
                </div>

                <h3 className="profile-name">
                  {selUser.user_name}
                </h3>

                <span
                  className={`badge ${
                    selUser.user_type === 1
                      ? "badge-admin"
                      : "badge-general"
                  }`}
                >
                  {selUser.user_type === 1
                    ? "Admin"
                    : "General User"}
                </span>

              </div>

              {/* DETAILS */}

              <div className="row g-3">

                {[
                  {
                    label: "Login ID",
                    value:
                      selUser.user_loginId,
                    icon: <> <FiLock/> </>,
                  },
                  {
                    label: "Email",
                    value:
                      selUser.user_emailId,
                    icon: <> <FiMail/> </>,
                  },
                  {
                    label: "Phone",
                    value:
                      selUser.user_telno ||
                      "—",
                    icon: <> <FiPhoneCall/> </>,
                  },
                  {
                    label: "Address",
                    value:
                      selUser.user_address ||
                      "—",
                    icon: <> <FiHome/> </>,
                  },
                  {
                    label: "Status",
                    value:
                      selUser.user_status === 1
                        ? "Active"
                        : "Inactive",
                    icon: <> <FiActivity/> </>,
                  },
                  {
                    label: "Created",
                    value:
                      selUser.user_createddate
                        ?.split("T")[0] ||
                      selUser.user_createddate ||
                      "—",
                    icon: <> <FiCalendar/> </>,
                  },
                ].map((r) => (

                  <div
                    key={r.label}
                    className="col-xl-3 col-lg-4 col-md-6 col-12"
                  >

                    <div className="profile-row">

                      <div className="profile-row-icon">
                        {r.icon}
                      </div>

                      <div>

                        <div className="profile-row-label">
                          {r.label}
                        </div>

                        <div className="profile-row-value">
                          {r.value}
                        </div>

                      </div>

                    </div>

                  </div>

                ))}

              </div>

              {/* MODULE ACCESS */}

              <div className="sec-div">
                <span>
                  Module Access
                </span>
              </div>

              {selUser.user_type === 1 ? (

                <div className="admin-badge">

                  <span className="admin-badge-icon">
                    🛡️
                  </span>

                  Full access to all modules

                </div>

              ) : (

                <div className="mod-grid">

                  {allMods.length === 0 && (
                    <p className="no-modules-text">
                      No modules found
                    </p>
                  )}

                  {allMods.map((mod) => {

                    const assigned =
                      viewMods.includes(
                        Number(mod.mod_id)
                      );

                    return (

                      <div
                        key={mod.mod_id}
                        className={`view-mod-chip ${
                          assigned
                            ? "assigned"
                            : "not-assigned"
                        }`}
                      >

                        <span>
                          {assigned
                            ? "✅"
                            : "⭕"}
                        </span>

                        {mod.mod_name ||
                          mod.mod_title ||
                          `Module ${mod.mod_id}`}

                      </div>

                    );

                  })}

                </div>

              )}

            </div>

            <div className="um-modal-foot">

              <button
                className="um-btn-secondary"
                onClick={() =>
                  setModal(null)
                }
              >
                Close
              </button>

              <button
                className="um-btn-primary"
                onClick={() =>
                  openEdit(selUser)
                }
              >
                <FiEdit />
                Edit User
              </button>

            </div>

          </div>

        </div>

      )}

      {/* =========================================
          DELETE MODAL
      ========================================== */}

      {modal === "delete" && selUser && (

        <div
          className="um-overlay"
          onClick={() => setModal(null)}
        >

          <div
            className="um-modal um-delete-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            <div className="delete-content">

              <div className="delete-icon">
                <FiTrash2 />
              </div>

              <h2 className="delete-title">
                Delete User?
              </h2>

              <p className="delete-description">

                Are you sure you want to delete{" "}

                <span className="badge badge-success">
                  {selUser.user_name}
                </span>

                ?

                <br />

                This action cannot be undone.

              </p>

              <div className="delete-actions">

                {/* CANCEL */}

                <button
                  className="um-btn-secondary"
                  onClick={() =>
                    setModal(null)
                  }
                  disabled={saving}
                >
                  Cancel
                </button>

                {/* DELETE */}

                <button
                  disabled={saving}
                  className="delete-confirm-btn"
                  onClick={handleDelete}
                >

                  {saving ? (
                    <span className="spinner" />
                  ) : (
                    <>
                      <FiTrash2 />
                      Delete Permanently
                    </>
                  )}

                </button>

              </div>

            </div>

          </div>

        </div>

      )}

    

    </Layout>
  );
}
